/**
 * Tenant host path planes — Public / Relationship / Business (R1 Phase 1)
 *
 * @see doc/system-completion-plan/01-target/RELATIONSHIP_PLANE_SEPARATION.md
 * @see doc/system-completion-plan/01-target/TENANT_HOST_PATH_PLANES.md
 */

import type { HostKind } from './hostContext';

export type PathPlane = 'platform' | 'public' | 'relationship' | 'business';

/** JWT entityType — Access Key kind (internal staff · external relation · site member). */
export type SiteEntityType = 'internal' | 'external' | 'user' | null;

/** Planes that use site-style auth shell + postLoginRedirect (public B2C + relationship). */
export type SiteAuthPathPlane = Extract<PathPlane, 'public' | 'relationship'>;

/** Planes accepted by resolveLoginSuccessPath. */
export type LoginSuccessPlane = SiteAuthPathPlane | 'business';

/** Business plane 경로 prefix (D14) */
export const BUSINESS_PLANE_PREFIXES = [
  '/entity',
  '/item',
  '/appeal',
  '/finance',
  '/finance-advanced',
  '/space',
  '/knowledge',
  '/calendar',
  '/log',
  '/marketing',
  '/flow',
  '/approvals',
  '/event',
  '/conscious',
  '/manufacturing',
  '/warehouse',
  '/inventory',
  '/automation',
  '/export',
  '/settings',
  '/physical-access',
  '/q',
  '/system',
  '/workspace',
  '/test',
  /**
   * `/today` (+ `/today/*`) is a business-plane alias mounted in MainRouter
   * (see `router/index.tsx`) that Navigates to `/workspace`. Without this
   * prefix the alias route is unreachable: TenantHostRouter classifies
   * `/today` as public and mounts SiteRouter instead of MainRouter, so the
   * alias never executes (N1 route-plane correction).
   */
  '/today',
] as const;

/** Relationship plane 경로 prefix — /portal URL (PathPlane: relationship) */
export const RELATIONSHIP_PLANE_PREFIXES = ['/portal'] as const;

/** 플랫폼 host 전용 경로 — tenant host에서 접근 시 platform으로 redirect */
export const PLATFORM_ONLY_PATH_PREFIXES = ['/onboarding', '/genesis'] as const;

/** Guest 비로그인 공개 Site 경로 prefix */
export const GUEST_PUBLIC_SITE_PREFIXES = [
  '/',
  '/about',
  '/products',
  '/services',
  '/contact',
  '/book',
  '/order',
  '/news',
  '/locations',
  '/policies',
  '/login',
  '/auth',
  '/join',
  '/member',
  '/partner',
  '/status',
  '/apply',
  '/campaigns',
] as const;

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  const withoutTrailing = pathname.replace(/\/+$/, '') || '/';
  return withoutTrailing.startsWith('/') ? withoutTrailing : `/${withoutTrailing}`;
}

/** pathname이 business plane prefix와 매칭되는지 확인한다. */
export function isBusinessPlanePath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return BUSINESS_PLANE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/** pathname이 relationship plane prefix와 매칭되는지 확인한다. */
export function isRelationshipPlanePath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return RELATIONSHIP_PLANE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

/** Public site plane path (not business, not relationship). */
export function isPublicSitePlanePath(pathname: string): boolean {
  return !isBusinessPlanePath(pathname) && !isRelationshipPlanePath(pathname);
}

/** pathname이 platform host 전용인지 확인한다. */
export function isPlatformOnlyPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return PLATFORM_ONLY_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

/** Guest(비로그인) 공개 Site 경로 여부 */
export function isGuestPublicSitePath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return GUEST_PUBLIC_SITE_PREFIXES.some((prefix) => {
    if (prefix === '/') return path === '/';
    return path === prefix || path.startsWith(`${prefix}/`);
  });
}

export function isSiteAuthPlane(plane: LoginSuccessPlane): plane is SiteAuthPathPlane {
  return plane === 'public' || plane === 'relationship';
}

/**
 * host kind + pathname으로 path plane을 반환한다.
 * - platform host: 모든 경로 → platform plane
 * - tenant host: business prefix → business, /portal → relationship, 그 외 → public
 */
export function matchPathPlane(pathname: string, hostKind: HostKind): PathPlane {
  if (hostKind === 'platform') return 'platform';
  if (isBusinessPlanePath(pathname)) return 'business';
  if (isRelationshipPlanePath(pathname)) return 'relationship';
  return 'public';
}

/**
 * Business plane 접근 가능 여부 (D2 · D22 · TP-G26).
 * - external entity → business plane 차단 (Relationship `/portal/*` 전용)
 * - internal / user → 허용
 * - entityType null(legacy JWT) → 차단
 */
export function canAccessBusinessPlane(entityType: SiteEntityType): boolean {
  if (entityType === null) return false;
  return entityType !== 'external';
}

/** 로그인 후 기본 redirect — Key type routing (R6). */
export function resolvePostLoginPath(entityType: SiteEntityType): string {
  if (entityType === 'internal') return '/workspace';
  if (entityType === 'external') return PORTAL_ENTRY_PATH;
  if (entityType === 'user') return '/my';
  return '/';
}

/** Counterparty portal 진입 경로 (legacy `/partner/*` → redirect) */
export const PORTAL_ENTRY_PATH = '/portal';

/** @deprecated Use PORTAL_ENTRY_PATH — legacy partner plane removed */
export const PARTNER_ENTRY_PATH = PORTAL_ENTRY_PATH;

export interface SitePostLoginRedirect {
  /** Site member Key (entityType user) — public plane default `/my` */
  member?: string;
  /** Alias for `member` (R6 key-type routing schema) */
  public?: string;
  /** External relation Key — relationship portal home default `/portal` */
  external: string;
  /** Internal staff Key — business workspace default `/workspace` */
  internal: string;
}

export const DEFAULT_SITE_POST_LOGIN_REDIRECT: Required<
  Pick<SitePostLoginRedirect, 'member' | 'external' | 'internal'>
> = {
  member: '/my',
  external: PORTAL_ENTRY_PATH,
  internal: '/workspace',
};

/** Legacy models: `internal` was member path — migrate member branch without breaking join CTAs. */
export function resolveSitePostLoginRedirect(
  redirect?: SitePostLoginRedirect
): Required<Pick<SitePostLoginRedirect, 'member' | 'external' | 'internal'>> {
  if (!redirect) return DEFAULT_SITE_POST_LOGIN_REDIRECT;

  const member =
    redirect.member ??
    redirect.public ??
    redirect.internal ??
    DEFAULT_SITE_POST_LOGIN_REDIRECT.member;
  const external = redirect.external ?? DEFAULT_SITE_POST_LOGIN_REDIRECT.external;
  const internal =
    redirect.member !== undefined
      ? redirect.internal || DEFAULT_SITE_POST_LOGIN_REDIRECT.internal
      : DEFAULT_SITE_POST_LOGIN_REDIRECT.internal;

  return { member, external, internal };
}

/** location.state.from — open redirect 방지 */
export function isSafeLoginRedirectPath(pathname: string): boolean {
  const trimmed = pathname.trim();
  if (!trimmed || trimmed.includes('://') || trimmed.includes(':')) return false;
  const path = normalizePathname(trimmed);
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path === '/login') return false;
  return true;
}

/**
 * 로그인 성공 후 redirect 경로 (LOGIN-SITE).
 * 우선순위: state.from → site postLoginRedirect → resolvePostLoginPath
 */
export function resolveLoginSuccessPath(options: {
  plane: LoginSuccessPlane;
  entityType: SiteEntityType;
  fromPath?: string;
  postLoginRedirect?: SitePostLoginRedirect;
}): string {
  const { plane, entityType, fromPath, postLoginRedirect } = options;

  if (fromPath && isSafeLoginRedirectPath(fromPath)) {
    return normalizePathname(fromPath);
  }

  if (postLoginRedirect) {
    const resolved = resolveSitePostLoginRedirect(postLoginRedirect);
    if (isSiteAuthPlane(plane)) {
      if (entityType === 'external') {
        return normalizePathname(resolved.external);
      }
      if (entityType === 'user') {
        return normalizePathname(resolved.member);
      }
      if (entityType === 'internal') {
        return normalizePathname(resolved.internal);
      }
    } else if (plane === 'business' && entityType === 'internal') {
      return normalizePathname(resolved.internal);
    }
  }

  return resolvePostLoginPath(entityType);
}

/** Internal 로그인 사용자용 Business plane 진입 경로 */
export const BUSINESS_ENTRY_PATH = '/entity';
